import * as React from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import XanoAPI from "../../../lib/XanoAPI";
import { Button } from "../../ui/button";
import { useToast } from "../../ui/use-toast";
import { ClientType } from "../../../types";
import { Icons } from "../../../utils/icons";
import moment from "moment";
import type { AutoSaveStatus } from "../../../hooks/useIntervalAutoSave";
import { Clock, Sparkles } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { useAuth } from "../../../lib/AuthContext";
// custom components
import ItemsComponent from "../../form-fields/ItemsComponent";
import CurrentIncome from "../components/current-income";
import FederalTSP from "../components/federal-tsp/federal-tsp";
import FederalSupplement from "../components/federal-supplement";
import FEGLIClient from "../components/fegli-client";
import SocialSecurity from "../components/social-security";
import FederalRetirementPlan from "../components/federal-retirement-plan";
import FederalSavingsPlan from "../components/federal-savings-plan";
import RetirementIncomeNeeded from "../components/retirement-income-needed";
import UnusedSickLeave from "../components/unused-sick-leave";
import FederalInformationTable from "../components/federal-information-table";

const formSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(), // id can be number or string
  user_id: z.number(), // user_id is required
  grossIncome: z.number().optional(),
  payPeriodsAnnual: z.number().optional(),
  expectedAnnualRaise: z.number().optional(),

  startPayments: z
    .enum([
      "retirement_or_62",
      "age_no_penalty_occurs",
      "do_not_calculate",
      "specified_age",
    ])
    .optional(),
  calculationMethod: z
    .enum(["calculate", "value_at_retirement", "todays_value"])
    .optional(),
  yearsIncomeSsi: z.number().optional(),
  maximumRetirementCola: z.number().optional(),
  retirementPlan: z.enum(["fers", "csrs", "csrs_offset"]).optional(),
  fersPlan: z.string().optional(),
  serviceComputationDate: z.string().nullable().optional(),
  startRetirementDistAt: z
    .enum(["no_penalty_age", "specific_age", "earliest_possible"])
    .optional(),
  ageToStartDistributionsAt: z.number().optional(),
  determineSocialSecurityMethod: z
    .enum(["calculate_value", "enter_age_62_value"])
    .optional(),
  monthlyIncomeAt62: z.number().optional(),
  supplementHasAnnualCola: z.boolean().optional(),
  includeAnnualLeaveBenefit: z.boolean().optional(),
  includeMilitaryService: z.boolean().optional(),

  contributionMethod: z
    .enum(["percent_of_pay", "dollar_amount", "maximum"])
    .optional(),
  pretaxCurrentBalance: z.number().optional(),
  contributionPerPay: z.number().optional(),
  preretireRateOfReturn: z.number().optional(),

  distributionMethod: z.enum(["use_funds_as_needed"]).optional(),
  distributionStartMethod: z.enum(["at_retirement", "at_age"]).optional(),
  distributionStartMethodAge: z.number().optional(),
  incomeModel: z.enum(["percent_of_income_at_retirement"]).optional(),

  neededAsPercentage: z.number().optional(),
  annualizedColaAdjustment: z.number().optional(),
  deductions: z.string().optional(),
  additionalIncome: z.string().optional(),
  allocationMethod: z
    .enum([
      "l_2065",
      "custom_gfcsi",
      "mixed",
      "l_2025",
      "l_2030",
      "l_2035",
      "l_2040",
      "l_2045",
      "l_2050",
      "l_2055",
      "l_2060",
    ])
    .optional(),
  includeRothAccount: z.boolean().optional(),
  allocations: z
    .array(
      z.object({
        fund: z.string().optional(),
        rateOfReturn: z.number().optional(),
        percentAllocated: z.number().optional(),
        currentBalance: z.number().optional(),
      })
    )
    .optional(),
  life_cycle_funds_allocations: z.string().optional(),
  // Fields from Federal TSP
  pretax_currentBalance: z.number().optional(),
  pretax_contributionPerPaycheck: z.number().optional(),
  pretax_preRetireRateOfReturn: z.number().optional(),
  pretax_futureContributionsMethod: z
    .enum(["none", "percent_of_pay", "dollars_per_deposit", "maximum"])
    .optional(),
  pretax_annualIncreaseMethod: z
    .enum(["no_increase", "increase_in_value", "percentage_increase"])
    .optional(),
  pretax_distributionMethod: z
    .enum([
      "use_funds_as_needed",
      "annuitize",
      "retain_principal",
      "amortize_no_inflation",
      "amortize_with_inflation",
      "percent_of_account",
      "rmd_only",
      "tsp_annuity",
      "dollar_value_no_inflation",
      "dollar_value_with_inflation",
    ])
    .optional(),
  pretax_rateOfReturn: z.number().optional(),
  pretax_distributionStartMethod: z
    .enum([
      "at_retirement",
      "specific_date",
      "owners_age",
      "years_after_retirement",
      "59_and_a_half",
    ])
    .optional(),

  roth_currentBalance: z.number().optional(),
  roth_contributionPerPaycheck: z.number().optional(),
  roth_preRetireRateOfReturn: z.number().optional(),
  roth_futureContributionsMethod: z
    .enum(["none", "percent_of_pay", "dollars_per_deposit", "maximum"])
    .optional(),
  roth_annualIncreaseMethod: z
    .enum(["no_increase", "increase_in_value", "percentage_increase"])
    .optional(),
  roth_distributionMethod: z
    .enum([
      "use_funds_as_needed",
      "annuitize",
      "retain_principal",
      "amortize_no_inflation",
      "amortize_with_inflation",
      "percent_of_account",
      "rmd_only",
      "tsp_annuity",
      "dollar_value_no_inflation",
      "dollar_value_with_inflation",
    ])
    .optional(),
  roth_rateOfReturn: z.number().optional(),
  roth_distributionStartMethod: z
    .enum([
      "at_retirement",
      "specific_date",
      "owners_age",
      "years_after_retirement",
      "59_and_a_half",
    ])
    .optional(),

  createdAt: z.string().optional(), // Timestamp as string or undefined
  federal_tsp_allocations: z.string().optional(),
  unused_sick_leave: z.string().optional(),
  // FEGLI fields
  fegli_basicCoverage: z.boolean().optional(),
  fegli_postalEmployee: z.boolean().optional(),
  fegli_reductionAtAge65: z.string().optional(),
  fegli_optionA: z.boolean().optional(),
  fegli_optionB: z.boolean().optional(),
  fegli_optionBMultiplier: z.string().optional(),
  fegli_optionBAge65Reduction: z.string().optional(),
  fegli_optionC: z.boolean().optional(),
  fegli_optionCMultiplier: z.string().optional(),
  fegli_optionCIncludeSpouse: z.boolean().optional(),
  fegli_optionCAge65Reduction: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function FinancialInformation({
  client,
  onUpdate,
  handleTabChange,
  setFormsDirty,
  fetchClientData,
  resetFormsDirtyState,
  formsDirty,
  saveStatus,
  lastSaveTime,
  isSmartPasteOpen,
  setIsSmartPasteOpen,
  setSmartPasteHandlers,
}: {
  client: ClientType;
  onUpdate: Function;
  handleTabChange: Function;
  setFormsDirty: Function;
  fetchClientData: Function;
  resetFormsDirtyState: Function;
  formsDirty: any;
  saveStatus: AutoSaveStatus;
  lastSaveTime: number;
  isSmartPasteOpen?: boolean;
  setIsSmartPasteOpen?: (open: boolean) => void;
  setSmartPasteHandlers?: (handlers: any) => void;
}) {
  React.useEffect(() => {
    onUpdate();
  }, []);
  const { toast } = useToast();
  const auth = useAuth();
  const isAutoSaving = React.useRef(false);
  
  // Smart Paste operation flag - prevents form reset during population
  const isSmartPastePopulating = React.useRef(false);
  
  // Refs for handler functions to avoid stale closures
  const handlersRef = React.useRef({
    onPersonalDataParsed: (data: Partial<ClientType>) => {},
    onFinancialDataParsed: (data: Partial<ClientType>) => {},
    onPopulateSingleField: (fieldName: string, value: any) => {},
    onSave: async () => {},
  });

  const [yearsIncomeSsiDiff, setYearsIncomeSsiDiff] = React.useState(0);
  const api = XanoAPI.getInstance();

  // Initialize react-hook-form with defaultValues
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange", // Enable real-time dirty tracking
    criteriaMode: "all", // Report all errors
    reValidateMode: "onChange", // Revalidate on every change
    defaultValues: {
      user_id: client?.user_id || api.getUserId(),
      grossIncome:
        client?.grossIncome ??
        auth?.currentUser?.user_preferences?.annual_raise,
      payPeriodsAnnual: client?.payPeriodsAnnual ?? 0,
      expectedAnnualRaise:
        client?.expectedAnnualRaise ||
        auth.currentUser?.user_preferences?.annual_raise,
      startPayments: client?.startPayments || "retirement_or_62",
      calculationMethod: client?.calculationMethod ?? "calculate",
      yearsIncomeSsi: client?.yearsIncomeSsi ?? 0,
      maximumRetirementCola: client?.maximumRetirementCola ?? 0,
      retirementPlan: client?.retirementPlan ?? "fers",
      serviceComputationDate: client?.serviceComputationDate ?? "",
      startRetirementDistAt: client?.startRetirementDistAt ?? "no_penalty_age",
      includeAnnualLeaveBenefit: client?.includeAnnualLeaveBenefit ?? false,
      includeMilitaryService: client?.includeMilitaryService ?? false,
      ageToStartDistributionsAt: client?.ageToStartDistributionsAt ?? 0,
      determineSocialSecurityMethod:
        client?.determineSocialSecurityMethod ?? "calculate_value",
      monthlyIncomeAt62: client?.monthlyIncomeAt62 ?? 0.0,
      supplementHasAnnualCola: client?.supplementHasAnnualCola ?? false,
      contributionMethod: client?.contributionMethod ?? "percent_of_pay",
      pretaxCurrentBalance: client?.pretaxCurrentBalance ?? 0,
      contributionPerPay: client?.contributionPerPay ?? 0,
      preretireRateOfReturn: client?.preretireRateOfReturn ?? 0,
      distributionMethod: client?.distributionMethod ?? "use_funds_as_needed",
      distributionStartMethod:
        client?.distributionStartMethod ?? "at_retirement",
      incomeModel: client?.incomeModel ?? "percent_of_income_at_retirement",
      neededAsPercentage: client?.neededAsPercentage ?? 0,
      annualizedColaAdjustment: client?.annualizedColaAdjustment ?? 0,
      deductions: client?.deductions,
      additionalIncome: client?.additionalIncome,
      allocationMethod: client?.allocationMethod || "l_2065",
      includeRothAccount: client?.includeRothAccount || false,
      allocations: client?.allocations,
      life_cycle_funds_allocations: client?.life_cycle_funds_allocations || "",
      unused_sick_leave:
        client?.unused_sick_leave ||
        JSON.stringify({
          averageHoursSavedBiweekly: 0,
          numberOfTwoWeekPeriods: 0,
          currentAccumulatedSickLeave: 0,
          hoursToBeCredited: 0,
          estimatedSickLeaveHoursAtRetirement: 0,
          hoursPerYear: 0,
          additionalYearsOfServiceCredited: "None",
        }),
      pretax_currentBalance: client?.pretax_currentBalance || 0.0,
      pretax_contributionPerPaycheck:
        client?.pretax_contributionPerPaycheck || 0.0,
      pretax_preRetireRateOfReturn: client?.pretax_preRetireRateOfReturn || 0.0,
      pretax_futureContributionsMethod:
        client?.pretax_futureContributionsMethod || "none",
      pretax_annualIncreaseMethod:
        client?.pretax_annualIncreaseMethod || "no_increase",
      pretax_distributionMethod:
        client?.pretax_distributionMethod || "use_funds_as_needed",
      pretax_rateOfReturn: client?.pretax_rateOfReturn || 0.0,
      pretax_distributionStartMethod:
        client?.pretax_distributionStartMethod || "at_retirement",
      roth_currentBalance: client?.roth_currentBalance || 0.0,
      roth_contributionPerPaycheck: client?.roth_contributionPerPaycheck || 0.0,
      roth_preRetireRateOfReturn: client?.roth_preRetireRateOfReturn || 0.0,
      roth_futureContributionsMethod:
        client?.roth_futureContributionsMethod || "none",
      roth_annualIncreaseMethod:
        client?.roth_annualIncreaseMethod || "no_increase",
      roth_distributionMethod:
        client?.roth_distributionMethod || "use_funds_as_needed",
      roth_rateOfReturn: client?.roth_rateOfReturn || 0.0,
      roth_distributionStartMethod:
        client?.roth_distributionStartMethod || "at_retirement",
      federal_tsp_allocations: client?.federal_tsp_allocations,
      fegli_basicCoverage: client?.fegli_basicCoverage ?? false,
      fegli_postalEmployee: client?.fegli_postalEmployee ?? false,
      fegli_reductionAtAge65: client?.fegli_reductionAtAge65 ?? "no_reduction",
      fegli_optionA: client?.fegli_optionA ?? false,
      fegli_optionB: client?.fegli_optionB ?? false,
      fegli_optionBMultiplier: client?.fegli_optionBMultiplier ?? "1",
      fegli_optionBAge65Reduction:
        client?.fegli_optionBAge65Reduction ?? "none",
      fegli_optionC: client?.fegli_optionC ?? false,
      fegli_optionCMultiplier: client?.fegli_optionCMultiplier ?? "1",
      fegli_optionCIncludeSpouse: client?.fegli_optionCIncludeSpouse ?? false,
      fegli_optionCAge65Reduction:
        client?.fegli_optionCAge65Reduction ?? "none",
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState,
    trigger,
  } = form;

  // Access formState properties directly for reactivity
  const { errors, isDirty, touchedFields, dirtyFields, isValid } = formState;

  // Register Smart Paste handlers with parent (must be at top level, not conditional)
  React.useEffect(() => {
    if (setSmartPasteHandlers) {
      console.log('📝 [Financial Tab] Registering Smart Paste handlers');
      setSmartPasteHandlers((prev: any) => ({
        ...prev,
        financial: {
          onPersonalDataParsed: (data: any) => {
            console.log('📞 [Financial Tab] onPersonalDataParsed wrapper called');
            return handlersRef.current.onPersonalDataParsed(data);
          },
          onFinancialDataParsed: (data: any) => {
            console.log('📞 [Financial Tab] onFinancialDataParsed wrapper called');
            return handlersRef.current.onFinancialDataParsed(data);
          },
          onPopulateSingleField: (fieldName: string, value: any) => {
            console.log('📞 [Financial Tab] onPopulateSingleField wrapper called');
            return handlersRef.current.onPopulateSingleField(fieldName, value);
          },
          onSave: () => {
            console.log('📞 [Financial Tab] onSave wrapper called');
            return handlersRef.current.onSave();
          },
          isSaveDisabled: !isValid || !isDirty,
        },
      }));
      console.log('✅ [Financial Tab] Handlers registered successfully');
    }
  }, [setSmartPasteHandlers, isValid, isDirty]);  // Include isValid/isDirty to update isSaveDisabled

  // Reset form when client changes (to update dirty tracking baseline)
  React.useEffect(() => {
    if (client?.id && !isSmartPastePopulating.current) {
      reset({
        user_id: client?.user_id || api.getUserId(),
        grossIncome:
          client?.grossIncome ??
          auth?.currentUser?.user_preferences?.annual_raise,
        payPeriodsAnnual: client?.payPeriodsAnnual ?? 0,
        expectedAnnualRaise:
          client?.expectedAnnualRaise ||
          auth.currentUser?.user_preferences?.annual_raise,
        startPayments: client?.startPayments || "retirement_or_62",
        calculationMethod: client?.calculationMethod ?? "calculate",
        yearsIncomeSsi: client?.yearsIncomeSsi ?? 0,
        maximumRetirementCola: client?.maximumRetirementCola ?? 0,
        retirementPlan: client?.retirementPlan ?? "fers",
        serviceComputationDate: client?.serviceComputationDate ?? "",
        startRetirementDistAt:
          client?.startRetirementDistAt ?? "no_penalty_age",
        includeAnnualLeaveBenefit: client?.includeAnnualLeaveBenefit ?? false,
        includeMilitaryService: client?.includeMilitaryService ?? false,
        ageToStartDistributionsAt: client?.ageToStartDistributionsAt ?? 0,
        determineSocialSecurityMethod:
          client?.determineSocialSecurityMethod ?? "calculate_value",
        monthlyIncomeAt62: client?.monthlyIncomeAt62 ?? 0.0,
        supplementHasAnnualCola: client?.supplementHasAnnualCola ?? false,
        contributionMethod: client?.contributionMethod ?? "percent_of_pay",
        pretaxCurrentBalance: client?.pretaxCurrentBalance ?? 0,
        contributionPerPay: client?.contributionPerPay ?? 0,
        preretireRateOfReturn: client?.preretireRateOfReturn ?? 0,
        distributionMethod: client?.distributionMethod ?? "use_funds_as_needed",
        distributionStartMethod:
          client?.distributionStartMethod ?? "at_retirement",
        incomeModel: client?.incomeModel ?? "percent_of_income_at_retirement",
        neededAsPercentage: client?.neededAsPercentage ?? 0,
        annualizedColaAdjustment: client?.annualizedColaAdjustment ?? 0,
        deductions: client?.deductions,
        additionalIncome: client?.additionalIncome,
        allocationMethod: client?.allocationMethod || "l_2065",
        includeRothAccount: client?.includeRothAccount || false,
        allocations: client?.allocations,
        life_cycle_funds_allocations:
          client?.life_cycle_funds_allocations || "",
        unused_sick_leave:
          client?.unused_sick_leave ||
          JSON.stringify({
            averageHoursSavedBiweekly: 0,
            numberOfTwoWeekPeriods: 0,
            currentAccumulatedSickLeave: 0,
            hoursToBeCredited: 0,
            estimatedSickLeaveHoursAtRetirement: 0,
            hoursPerYear: 0,
            additionalYearsOfServiceCredited: "None",
          }),
        pretax_currentBalance: client?.pretax_currentBalance || 0.0,
        pretax_contributionPerPaycheck:
          client?.pretax_contributionPerPaycheck || 0.0,
        pretax_preRetireRateOfReturn:
          client?.pretax_preRetireRateOfReturn || 0.0,
        pretax_futureContributionsMethod:
          client?.pretax_futureContributionsMethod || "none",
        pretax_annualIncreaseMethod:
          client?.pretax_annualIncreaseMethod || "no_increase",
        pretax_distributionMethod:
          client?.pretax_distributionMethod || "use_funds_as_needed",
        pretax_rateOfReturn: client?.pretax_rateOfReturn || 0.0,
        pretax_distributionStartMethod:
          client?.pretax_distributionStartMethod || "at_retirement",
        roth_currentBalance: client?.roth_currentBalance || 0.0,
        roth_contributionPerPaycheck:
          client?.roth_contributionPerPaycheck || 0.0,
        roth_preRetireRateOfReturn: client?.roth_preRetireRateOfReturn || 0.0,
        roth_futureContributionsMethod:
          client?.roth_futureContributionsMethod || "none",
        roth_annualIncreaseMethod:
          client?.roth_annualIncreaseMethod || "no_increase",
        roth_distributionMethod:
          client?.roth_distributionMethod || "use_funds_as_needed",
        roth_rateOfReturn: client?.roth_rateOfReturn || 0.0,
        roth_distributionStartMethod:
          client?.roth_distributionStartMethod || "at_retirement",
        federal_tsp_allocations: client?.federal_tsp_allocations,
        fegli_basicCoverage: client?.fegli_basicCoverage ?? false,
        fegli_postalEmployee: client?.fegli_postalEmployee ?? false,
        fegli_reductionAtAge65:
          client?.fegli_reductionAtAge65 ?? "no_reduction",
        fegli_optionA: client?.fegli_optionA ?? false,
        fegli_optionB: client?.fegli_optionB ?? false,
        fegli_optionBMultiplier: client?.fegli_optionBMultiplier ?? "1",
        fegli_optionBAge65Reduction:
          client?.fegli_optionBAge65Reduction ?? "none",
        fegli_optionC: client?.fegli_optionC ?? false,
        fegli_optionCMultiplier: client?.fegli_optionCMultiplier ?? "1",
        fegli_optionCIncludeSpouse: client?.fegli_optionCIncludeSpouse ?? false,
        fegli_optionCAge65Reduction:
          client?.fegli_optionCAge65Reduction ?? "none",
      });
      // Trigger validation after reset to sync isValid state
      setTimeout(() => trigger(), 0);
    }
  }, [client?.id, reset, trigger]);

  const watchAtAge = watch("startRetirementDistAt");
  const watchContributionMethod = watch("contributionMethod");
  const watchDeductions = watch("deductions");
  const watchAdditionalIncome = watch("additionalIncome");
  const watchUnusedSickLeave = watch("unused_sick_leave");
  const watchRetirementPlan = watch("retirementPlan");
  const watchDistributionStartMethod = watch("distributionStartMethod");
  const watchServiceComputationDate = watch("serviceComputationDate");
  const watchYearsIncomeSsi = watch("yearsIncomeSsi");

  React.useEffect(() => {
    if (watchServiceComputationDate) {
      // Try to parse the date with multiple formats
      const formats = [
        "YYYY-MM-DD",
        "MM/DD/YYYY",
        "DD/MM/YYYY",
        "YYYY/MM/DD",
        "MM-DD-YYYY",
        "DD-MM-YYYY",
        "YYYY-MM-DDTHH:mm:ss.SSSZ", // ISO format with timezone
        "YYYY-MM-DDTHH:mm:ssZ", // ISO format without milliseconds
        "YYYY-MM-DDTHH:mm:ss", // ISO format without timezone
      ];

      // Trim whitespace and check for empty string
      const trimmedDate = watchServiceComputationDate.trim();
      if (!trimmedDate) {
        console.log("SCD DEBUG: serviceComputationDate is empty after trim");
        return;
      }

      console.log(
        "SCD DEBUG: Raw serviceComputationDate value:",
        JSON.stringify(trimmedDate)
      );

      let parsedMoment = moment(trimmedDate);

      // If the default parsing fails, try alternative formats
      if (!parsedMoment.isValid()) {
        console.log(
          "SCD DEBUG: Default parsing failed, trying alternative formats"
        );
        for (const format of formats) {
          parsedMoment = moment(trimmedDate, format, true);
          if (parsedMoment.isValid()) {
            console.log(
              "SCD DEBUG: Format parsing successful:",
              format,
              parsedMoment.format()
            );
            break;
          }
        }
      } else {
        console.log(
          "SCD DEBUG: Default parsing successful:",
          parsedMoment.format()
        );
      }

      if (parsedMoment.isValid()) {
        const value = moment().diff(parsedMoment, "years");
        setYearsIncomeSsiDiff(value);
      } else {
        console.warn(
          "Could not parse service computation date:",
          watchServiceComputationDate
        );
        setYearsIncomeSsiDiff(0);
      }
    }
  }, [watchServiceComputationDate, yearsIncomeSsiDiff]);

  // Sync React Hook Form's isDirty state with parent's formsDirty state
  React.useEffect(() => {
    if (isDirty && dirtyFields) {
      const dirtyFieldNames = Object.keys(dirtyFields);
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: dirtyFieldNames,
      }));
    } else {
      // Clear dirty state when form is clean
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: [],
      }));
    }
  }, [isDirty, dirtyFields]);

  const onSubmit = async (data: FormData) => {
    if (!client || !client.id) {
      console.error("Client ID is missing.");
      return;
    }

    const processedData = {
      ...client,
      ...data,
    };

    try {
      await api.updateClient(client.id, processedData);

      // Fetch updated data immediately
      await fetchClientData(client.id);

      // Reset form to clear dirty state
      reset(data);

      // Clear the parent's dirty state
      resetFormsDirtyState();

      // Only show toast notification on manual save, not auto-save
      if (!isAutoSaving.current) {
        toast({
          title: "Financial Information Updated!",
          description: "Your client's financial information has been updated.",
        });
      }
      
      // Reset the auto-save flag
      isAutoSaving.current = false;
    } catch (error) {
      console.error("Error updating financial information:", error);
    }
  };

  const onSave = (items: string, field: any): void => {
    if (items) setValue(field, JSON.stringify({ values: items }));
  };

  // Handle single field population from Smart Paste
  const handlePopulateSingleField = (fieldName: string, value: any) => {
    isSmartPastePopulating.current = true;
    
    try {
      setValue(fieldName as any, value, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
      
      // Update parent dirty state
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: [...(prev.financialInformation || []), fieldName],
      }));
      
      console.log(`✅ Financial field applied: ${fieldName} = ${value}`);
    } catch (err) {
      console.error(`Failed to apply financial field ${fieldName}:`, err);
    }
    
    setTimeout(() => {
      isSmartPastePopulating.current = false;
    }, 100);
  };
  
  // Update handler refs
  handlersRef.current.onPopulateSingleField = handlePopulateSingleField;

  // Handle Smart Paste - Financial Information
  const handleFinancialDataParsed = (parsedData: Partial<ClientType>) => {
    console.log("🤖 [Financial Tab] Smart Paste: Populating", Object.keys(parsedData).length, "financial fields");

    // Remove fields that require specific JSON structure (ItemsComponent fields)
    const fieldsToRemove = ['deductions', 'additionalIncome', 'allocations', 'federal_tsp_allocations', 'unused_sick_leave'];
    fieldsToRemove.forEach(field => delete parsedData[field as keyof ClientType]);

    // Set Smart Paste flag to prevent form reset useEffect from firing
    isSmartPastePopulating.current = true;
    
    // Populate all fields with shouldDirty: true to mark them as changed
    Object.entries(parsedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        try {
          setValue(key as any, value, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });
        } catch (err) {
          console.error(`Failed to set ${key}:`, err);
        }
      }
    });
    
    // Finalize after a small delay
    setTimeout(() => {
      trigger();
      setFormsDirty((prev: any) => ({
        ...prev,
        financialInformation: Object.keys(parsedData),
      }));
      isSmartPastePopulating.current = false;
      console.log("✅ Smart Paste: Financial population complete");
    }, 100);
  };
  
  // Update handler refs
  handlersRef.current.onFinancialDataParsed = handleFinancialDataParsed;
  handlersRef.current.onPersonalDataParsed = () => {
    console.log("⚠️ Personal data parsed in financial tab - ignored");
  };
  handlersRef.current.onSave = async () => {
    console.log("💾 Smart Paste Save triggered (Financial)");
    return handleSubmit(onSubmit)();
  };

  // Real-time auto-save text with live updates
  const [currentTime, setCurrentTime] = React.useState(Date.now());
  
  // Update current time every second for real-time display
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getAutoSaveText = () => {
    if (lastSaveTime === 0) return null;
    
    const timeSinceLastSave = currentTime - lastSaveTime;
    const seconds = Math.floor(timeSinceLastSave / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      return `Autosaved ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (seconds > 5) {
      return `Autosaved ${seconds} seconds ago`;
    } else if (seconds > 0) {
      return "Autosaved just now";
    }
    return null;
  };

  return (
    <div className="py-6 mb-8 financial-details">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit, (errors) => {
            console.log("Submit was attempted", errors);
          })}
        >
          <CurrentIncome client={client} />

          <ItemsComponent
            json={watchAdditionalIncome}
            field="additionalIncome"
            onSave={onSave}
            sectionTitle="Additional Income"
            sectionId="additional-income"
          />

          <ItemsComponent
            json={watchDeductions}
            field="deductions"
            onSave={onSave}
            sectionTitle="Deductions"
            sectionId="deductions"
          />

          <FederalRetirementPlan
            client={client}
            watchAtAge={watchAtAge}
            watchRetirementPlan={watchRetirementPlan}
          />
          <SocialSecurity
            client={client}
            yearsIncomeSsiDiff={yearsIncomeSsiDiff}
          />

          <FederalInformationTable client={client} />
          <FederalSupplement client={client} />
          <RetirementIncomeNeeded client={client} />
          <FEGLIClient client={client} />
          <UnusedSickLeave formData={watchUnusedSickLeave} />
          <FederalTSP client={client} />

          <div className="fixed bottom-0 right-0 m-3 z-50 flex items-center gap-3">
            {getAutoSaveText() && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-md border border-gray-200">
                <Clock className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-xs text-gray-600">
                  {getAutoSaveText()}
                </span>
              </div>
            )}
            {setIsSmartPasteOpen && (
              <Button
                type="button"
                variant="outline"
                className="p-3"
                onClick={() => setIsSmartPasteOpen(true)}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Smart Paste
              </Button>
            )}
            <Button
              id="save-financial-information"
              type="submit"
              className="p-3 bg-accent"
              disabled={!isValid || !isDirty}
              onClick={(e) => {
                // Check if this is a manual click (has isTrusted property)
                if (e.isTrusted) {
                  isAutoSaving.current = false;
                } else {
                  // Programmatic click (auto-save)
                  isAutoSaving.current = true;
                }
              }}
            >
              <Icons.save className="mr-2" />
              {isDirty ? "Save Changes" : "No Changes"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
